import { adminApi } from '../../api/adminApi';
import { fetchAllPages } from '../../api/fetchAllPages';
import { adminBrokerGroupsApi } from '../../brokerGroups/api/brokerGroupsApi';
import { afterSuccess, invalidateCrmUsers } from '../../api/crmCaches';

/**
 * Adds what the users table sorts, filters and exports by:
 * - primaryBrokerGroupId: `brokerGroup` (a doc reference, listed as its id), or else
 *   `brokerGroupId`
 * - brokerGroups: [{ id, name }] for the primary (first) and then `brokerGroupArray`,
 *   without duplicates. A broker group that no longer exists is named by its id.
 * - ledger: the primary broker group's (a user's broker groups should all be on one ledger)
 */
const withBrokerGroupDetails = (user, brokerGroupsById) => {
	const primaryBrokerGroupId = user.brokerGroup ?? user.brokerGroupId ?? null;
	const ids = [...new Set([primaryBrokerGroupId, ...(user.brokerGroupArray ?? [])])].filter(
		Boolean,
	);
	return {
		...user,
		primaryBrokerGroupId,
		brokerGroups: ids.map((id) => ({ id, name: brokerGroupsById.get(id)?.name ?? id })),
		ledger: brokerGroupsById.get(primaryBrokerGroupId)?.ledger,
	};
};

/**
 * Admin user endpoints. listAllUsers loads every user (for the users table, pickers and
 * names), cached and shared by everything that needs it. Each item also gets
 * primaryBrokerGroupId, brokerGroups and ledger (see withBrokerGroupDetails), from the
 * cached broker group list. User import endpoints are in userImportsApi.js.
 */
export const adminUsersApi = adminApi.injectEndpoints({
	endpoints: (builder) => ({
		listAllUsers: builder.query({
			queryFn: async (arg, { dispatch }, extraOptions, baseQuery) => {
				const [result, { data: brokerGroups = [] }] = await Promise.all([
					fetchAllPages(baseQuery, '/users/list'),
					dispatch(
						adminBrokerGroupsApi.endpoints.listAllBrokerGroups.initiate(undefined, {
							subscribe: false,
						}),
					),
				]);
				if (result.error) {
					return result;
				}
				const brokerGroupsById = new Map(
					brokerGroups.map((brokerGroup) => [brokerGroup.id, brokerGroup]),
				);
				return {
					data: result.data.map((user) => withBrokerGroupDetails(user, brokerGroupsById)),
				};
			},
			providesTags: (result) => [
				{ type: 'ADMIN_USER', id: 'LIST' },
				...(result ?? []).map(({ id }) => ({ type: 'ADMIN_USER', id })),
			],
			// Fill getUser for every user, so single readers need no request
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const { data: users } = await queryFulfilled;
					dispatch(
						adminUsersApi.util.upsertQueryEntries(
							users.map((user) => ({ endpointName: 'getUser', arg: user.id, value: user })),
						),
					);
				} catch {
					// The list query reports its own error
				}
			},
		}),
		// One user, as listAllUsers gives it (with its broker group details). Normally filled
		// in by the list; if read before the list has loaded (or after an update invalidates
		// it), it loads the list and picks the user.
		getUser: builder.query({
			queryFn: async (userId, { dispatch }) => {
				const { data: users, error } = await dispatch(
					adminUsersApi.endpoints.listAllUsers.initiate(undefined, { subscribe: false }),
				);
				if (error) {
					return { error };
				}
				return { data: users.find((user) => user.id === userId) ?? null };
			},
			providesTags: (result, error, userId) => [{ type: 'ADMIN_USER', id: userId }],
		}),
		/**
		 * POST /users/create (see the backend's users/create): name, email and brokerGroupId,
		 * plus any of the fields updateUser takes. Responds with the new user's doc (its id
		 * and data). Refreshes the list. A 400 can carry { fields: { [field]: message } }.
		 */
		createUser: builder.mutation({
			query: (user) => ({
				url: '/users/create',
				method: 'POST',
				body: user,
			}),
			invalidatesTags: [{ type: 'ADMIN_USER', id: 'LIST' }],
			// The CRM's own copies of users, so pickers offer them
			onQueryStarted: afterSuccess(invalidateCrmUsers),
		}),
		/**
		 * PATCH /users/:id with only the changed fields (see the backend's
		 * services/validation/user.js). Refreshes the user and the list. A 400 can carry
		 * { fields: { [field]: message } }.
		 */
		updateUser: builder.mutation({
			query: ({ id, ...changes }) => ({
				url: `/users/${id}`,
				method: 'PATCH',
				body: changes,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'ADMIN_USER', id },
				{ type: 'ADMIN_USER', id: 'LIST' },
			],
			// The CRM's own copies of users, too
			onQueryStarted: afterSuccess(invalidateCrmUsers),
		}),
		/**
		 * Deactivates a user (isActive false: they can't sign in, their history stays) or
		 * activates them again: POST /users/:id/deactivate or /reactivate. Refreshes the user
		 * and the list.
		 */
		setUserActive: builder.mutation({
			query: ({ id, isActive }) => ({
				url: `/users/${id}/${isActive ? 'reactivate' : 'deactivate'}`,
				method: 'POST',
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'ADMIN_USER', id },
				{ type: 'ADMIN_USER', id: 'LIST' },
			],
			// The CRM's own copies of users too, so pickers stop (or start) offering them
			onQueryStarted: afterSuccess(invalidateCrmUsers),
		}),
	}),
});

export const {
	useListAllUsersQuery,
	useGetUserQuery,
	useCreateUserMutation,
	useUpdateUserMutation,
	useSetUserActiveMutation,
} = adminUsersApi;

/** Whether a users list item is active: admins deactivate users with isActive false; unset counts as active. */
export const isUserActive = (user) => user.isActive !== false;

/** A users list item's primary broker group ({ id, name }), if it has one. */
export const getPrimaryBrokerGroup = (user) =>
	user.primaryBrokerGroupId ? user.brokerGroups[0] : undefined;

/** A users list item's other broker groups ([{ id, name }]): its brokerGroupArray without the primary. */
export const getOtherBrokerGroups = (user) =>
	user.primaryBrokerGroupId ? user.brokerGroups.slice(1) : user.brokerGroups;
